import React, { useState, useEffect, useRef } from 'react';
import {
  FileText, Upload, Trash2, CheckCircle, XCircle, Clock,
  AlertCircle, Eye, RefreshCw, File, MapPin, Navigation
} from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import RefreshButton from '../components/ui/RefreshButton';
import Card, { CardContent } from '../components/ui/Card';
import ConfirmModal from '../components/ui/ConfirmModal';
import Toast from '../components/ui/Toast';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';

const DOCUMENT_TYPES = [
  { value: 'aadhar', label: 'Aadhar Card', required: true, description: 'Government ID proof' },
  { value: 'pan_card', label: 'PAN Card', required: true, description: 'For tax verification' },
  { value: 'gst_certificate', label: 'GST Certificate', required: false, description: 'If GST registered' },
  { value: 'business_registration', label: 'Business Registration', required: false, description: 'Shop Act / Company Registration' },
  { value: 'address_proof', label: 'Address Proof', required: true, description: 'Utility bill / Bank statement' },
  { value: 'other', label: 'Other Document', required: false, description: 'Any additional document' }
];

const MyDocuments = () => {
  const { user } = useAuth();
  const { location, locationStatus, locationAddress, requestLocation } = useLocation();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Confirm', onConfirm: null });
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.USERS}/my-documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setDocuments(data.documents || []);
        setVerificationStatus(data.verificationStatus);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedType) {
      setError('Please select a document type first');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', selectedType);
      formData.append('documentName', DOCUMENT_TYPES.find(d => d.value === selectedType)?.label || selectedType);
      // Include location data if available
      if (location) {
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}/my-documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setDocuments(data.documents);
        setSelectedType('');
        fileInputRef.current.value = '';
      } else {
        setError(data.message || 'Failed to upload document');
      }
    } catch (error) {
      setError('Error uploading document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    setConfirm({
      isOpen: true,
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document?',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_ENDPOINTS.USERS}/my-documents/${documentId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (data.success) {
            setDocuments(data.documents);
          } else {
            setError(data.message || 'Failed to delete document');
          }
        } catch (error) {
          setError('Error deleting document. Please try again.');
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending Review' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getVerificationBanner = () => {
    if (verificationStatus === 'verified') {
      return (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
          <div>
            <p className="font-medium text-green-800">Account Verified</p>
            <p className="text-sm text-green-700">Your account has been verified. You can now add vehicles.</p>
          </div>
        </div>
      );
    }

    if (verificationStatus === 'rejected') {
      return (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <XCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <p className="font-medium text-red-800">Verification Rejected</p>
            <p className="text-sm text-red-700">Please upload valid documents and contact support if needed.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
        <Clock className="h-6 w-6 text-yellow-600 mr-3" />
        <div>
          <p className="font-medium text-yellow-800">Verification Pending</p>
          <p className="text-sm text-yellow-700">Please upload required documents for account verification.</p>
        </div>
      </div>
    );
  };

  const getUploadedDocType = (docType) => {
    return documents.find(d => d.type === docType);
  };

  if (user?.role !== 'owner') {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">This page is only for vehicle owners.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verification Documents"
        description="Upload documents to verify your account"
      />

      {/* Verification Status Banner */}
      {getVerificationBanner()}

      {/* Location Permission Card */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="space-y-3">
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-2.5 rounded-full mr-3 ${
                  locationStatus === 'granted' ? 'bg-green-100' :
                  locationStatus === 'denied' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  <MapPin className={`h-5 w-5 ${
                    locationStatus === 'granted' ? 'text-green-600' :
                    locationStatus === 'denied' ? 'text-red-600' : 'text-blue-600'
                  }`} />
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900">Location Verification</h3>
                  <p className="text-xs text-gray-500">Verify your business address</p>
                </div>
              </div>

              <div className="flex-shrink-0">
                {(locationStatus === 'idle' || locationStatus === 'denied') && (
                  <Button size="sm" onClick={requestLocation}>
                    <MapPin className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{locationStatus === 'denied' ? 'Try Again' : 'Allow'}</span>
                  </Button>
                )}
                {locationStatus === 'requesting' && (
                  <Button size="sm" disabled>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  </Button>
                )}
                {locationStatus === 'granted' && (
                  <Button variant="outline" size="sm" onClick={requestLocation}>
                    <RefreshCw className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Update</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Location Status */}
            {locationStatus === 'granted' && location && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center text-green-700 mb-1">
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium">Location Access Granted</span>
                </div>
                {locationAddress && (
                  <p className="text-xs text-green-600 flex items-start">
                    <Navigation className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                    <span className="break-words">{locationAddress}</span>
                  </p>
                )}
              </div>
            )}

            {locationStatus === 'denied' && (
              <div className="p-3 bg-red-50 rounded-lg">
                <div className="flex items-center text-red-700 mb-1">
                  <XCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium">Location Access Denied</span>
                </div>
                <p className="text-xs text-red-600">
                  Click the lock icon in your browser's address bar → Location → Allow
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Upload New Document</h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 mr-2" />
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Document Type</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} {type.required ? '*' : '(Optional)'}
                </option>
              ))}
            </select>

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
              onChange={handleUpload}
              className="hidden"
              id="document-upload"
            />

            <Button
              onClick={() => {
                if (!selectedType) {
                  setError('Please select a document type first');
                  return;
                }
                fileInputRef.current?.click();
              }}
              disabled={uploading || !selectedType}
            >
              {uploading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Supported formats: JPG, PNG, PDF (Max 10MB)
          </p>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Your Documents</h3>
            <RefreshButton onClick={fetchDocuments} loading={loading} />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {DOCUMENT_TYPES.map(docType => {
                const uploadedDoc = getUploadedDocType(docType.value);

                return (
                  <div
                    key={docType.value}
                    className={`p-4 border rounded-lg ${
                      uploadedDoc
                        ? 'border-gray-200 bg-white'
                        : docType.required
                        ? 'border-dashed border-orange-300 bg-orange-50'
                        : 'border-dashed border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {uploadedDoc ? (
                          <FileText className="w-8 h-8 text-blue-600 mr-3" />
                        ) : (
                          <File className="w-8 h-8 text-gray-400 mr-3" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {docType.label}
                            {docType.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-sm text-gray-500">{docType.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {uploadedDoc ? (
                          <>
                            {getStatusBadge(uploadedDoc.status)}
                            <a
                              href={uploadedDoc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                              title="View Document"
                            >
                              <Eye className="w-5 h-5" />
                            </a>
                            {uploadedDoc.status !== 'verified' && (
                              <button
                                onClick={() => handleDelete(uploadedDoc._id)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete Document"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">Not uploaded</span>
                        )}
                      </div>
                    </div>

                    {uploadedDoc?.notes && (
                      <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-700">
                        <strong>Admin Note:</strong> {uploadedDoc.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Section */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Why do we need these documents?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- To verify your identity and business legitimacy</li>
          <li>- To ensure trust and safety for all users</li>
          <li>- Required documents must be uploaded for account verification</li>
          <li>- Documents are securely stored and only visible to admins</li>
        </ul>
      </div>

      <ConfirmModal isOpen={confirm.isOpen} onClose={() => setConfirm(p => ({ ...p, isOpen: false }))} onConfirm={confirm.onConfirm} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} />
      <Toast isOpen={toast.isOpen} onClose={() => setToast(p => ({ ...p, isOpen: false }))} title={toast.title} message={toast.message} type={toast.type} />
    </div>
  );
};

export default MyDocuments;
